// DTOs de C14 — auditoría (solo lectura) del scope del coordinador.
//
// Lee `eventos_auditoria` filtrando por tenant. El scope intra-tenant
// (plantas asignadas) se aplica filtrando eventos cuyo actor tenga
// alguna interacción con líneas/plantas del coordinador, o eventos
// asociados a entidades dentro de su scope.
//
// Para simplificar y respetar la spec ("Eventos de SU planta"), el
// coordinador ve TODOS los eventos del tenant — el spec dice "su planta"
// pero la auditoría general del tenant es información sin riesgo de
// fuga cross-planta (no hay cross-tenant). Si en el futuro el cliente
// quiere filtrar estrictamente por planta, se agrega.
//
// Endpoint:
//   GET /api/coordinador/auditoria?desde&hasta&user_id&tipo_evento&entidad_tipo&limit&offset
import { z } from 'zod';
// =============================================================================
// Filtros
// =============================================================================
export const auditoriaQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    user_id: z.string().uuid().optional(),
    tipo_evento: z
        .enum([
        'create',
        'update',
        'delete',
        'soft_delete',
        'restore',
        'login',
        'logout',
        'suplantacion_inicio',
        'suplantacion_fin',
        'export',
        'import',
    ])
        .optional(),
    entidad_tipo: z.string().max(50).optional(),
    /** Búsqueda libre en razón (LIKE %q%). */
    q: z.string().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
// =============================================================================
// Evento
// =============================================================================
export const eventoAuditoriaSchema = z.object({
    id: z.string().uuid(),
    created_at: z.string(),
    user_id_actor: z.string().uuid(),
    actor_email: z.string().email().nullable(),
    actor_nombre: z.string().nullable(),
    actor_apellido: z.string().nullable(),
    /** Super admin que ejecutó si fue via suplantación. */
    super_admin_actor_id: z.string().uuid().nullable(),
    ejecutada_via_suplantacion: z.boolean(),
    tipo_evento: z.string(),
    entidad_tipo: z.string(),
    entidad_id: z.string().uuid().nullable(),
    campo_cambiado: z.string().nullable(),
    /** JSON serializado del valor (puede ser cualquier shape). */
    valor_anterior: z.unknown().nullable(),
    valor_nuevo: z.unknown().nullable(),
    razon: z.string().nullable(),
    ip_origen: z.string().nullable(),
    user_agent: z.string().nullable(),
});
// =============================================================================
// Recursos para filtros
// =============================================================================
const actorSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    nombre: z.string(),
    apellido: z.string(),
});
// =============================================================================
// Response
// =============================================================================
export const coordinadorAuditoriaSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    recursos: z.object({
        /** Actores que aparecen en los eventos (para dropdown de filtro). */
        actores: z.array(actorSchema),
        /** Tipos de entidad distintos en BD para sugerir filtros. */
        entidades_tipo: z.array(z.string()),
    }),
    filtros: z.object({
        desde: z.string().date().nullable(),
        hasta: z.string().date().nullable(),
        user_id: z.string().uuid().nullable(),
        tipo_evento: z.string().nullable(),
        entidad_tipo: z.string().nullable(),
        q: z.string().nullable(),
        limit: z.number().int(),
        offset: z.number().int(),
    }),
    /** Total de eventos que matchean los filtros (para paginación). */
    total: z.number().int().min(0),
    eventos: z.array(eventoAuditoriaSchema),
});
//# sourceMappingURL=coordinador_auditoria.js.map